import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MailOpen, Scale, Landmark, HandCoins, ChevronLeft, Inbox } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* City Mail — official post, preinstalled on every phone. Court summons and verdicts,
   fines, and pawn-shop payout letters land here. The city writes; you can't reply. */

const KIND_ICON = { court: Scale, fine: Landmark, pawn: HandCoins, city: Mail };
const KIND_COLOR = { court: "#e8b93e", fine: "#ef4444", pawn: "#4ade80", city: "#38bdf8" };

const iso = (minAgo) => new Date(Date.now() - minAgo * 60000).toISOString();
export const MAIL_DEMO = [
  { id: "dm1", kind: "pawn", subject: "Sold: 1x Designer Watch", from_name: "Goldman & Sons Pawn", read: false, at: iso(42),
    body: "Ray Dalton took your 1x Designer Watch off the shelf for $1,500. After the house cut that's $1,350 — already in your bank.",
    meta: { payout: 1350 } },
  { id: "dm2", kind: "court", subject: "Summons — case CRT-26-014", from_name: "Bornwood Courthouse", read: false, at: iso(190),
    body: "You have been charged with: Reckless driving. Case CRT-26-014 is awaiting a hearing at the Bornwood Courthouse. Attend when the gavel calls — failure to appear becomes a warrant.",
    meta: { docket: "CRT-26-014" } },
  { id: "dm3", kind: "fine", subject: "Verdict in case CRT-26-009", from_name: "Bornwood Courthouse", read: true, at: iso(1500),
    body: "The court has delivered its verdict: GUILTY. A fine of $750 has been lodged against your account. Notes: second offence this month.",
    meta: { docket: "CRT-26-009", fine: 750 } },
];

export default function MailApp({ demo, serverId, token, accent, sfx }) {
  const [mail, setMail] = useState(demo ? MAIL_DEMO : []);
  const [open, setOpen] = useState(null); // mail doc

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/city/mail`, { params: { token } })
      .then((r) => setMail(r.data.mail)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const openLetter = (m) => {
    sfx?.keyTick();
    setOpen(m);
    if (!m.read) {
      setMail((ms) => ms.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
      if (!demo) api.post(`/nc/servers/${serverId}/city/mail/${m.id}/read`, {}, { params: { token } }).catch(() => {});
    }
  };

  if (open) {
    const Icon = KIND_ICON[open.kind] || Mail;
    const color = KIND_COLOR[open.kind] || "#38bdf8";
    return (
      <div data-testid="mail-letter" className="flex flex-col min-h-full">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10">
          <button data-testid="mail-back" onClick={() => setOpen(null)} className="text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}22`, border: `1px solid ${color}44` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-sans font-semibold text-sm text-white truncate">{open.subject}</p>
            <p className="font-mono text-[7px] text-white/40 uppercase tracking-[0.15em]">{open.from_name} · {String(open.at).slice(5, 16).replace("T", " ")}</p>
          </div>
        </div>
        <div className="flex-1 p-4 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          <p className="font-mono text-[10px] text-white/85 leading-relaxed whitespace-pre-wrap">{open.body}</p>
          {open.meta?.payout > 0 && (
            <div className="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-3 flex items-center gap-2">
              <HandCoins className="w-4 h-4 text-emerald-300" />
              <p className="font-mono text-[10px] text-emerald-200">Payout deposited: <span className="font-bold">${open.meta.payout.toLocaleString()}</span></p>
            </div>
          )}
          {open.meta?.docket && (
            <p className="mt-3 font-mono text-[8px] uppercase tracking-[0.2em] text-white/40">Ref: {open.meta.docket}</p>
          )}
          <p className="mt-6 font-mono text-[7px] text-white/30 uppercase tracking-[0.2em] text-center">Official city post — replies are not accepted</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="mail-app" className="p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2 px-1 pb-1">
        <Mail className="w-4 h-4" style={{ color: accent }} />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">City Mail</p>
        <p className="ml-auto font-mono text-[7px] text-white/40 uppercase tracking-[0.1em]">Bornwood Post</p>
      </div>
      {mail.length === 0 && (
        <div className="flex flex-col items-center mt-12 gap-2">
          <Inbox className="w-6 h-6 text-white/20" />
          <p data-testid="mail-empty" className="font-mono text-[10px] text-white/40 text-center">No letters — the city hasn't written to you yet</p>
        </div>
      )}
      {mail.map((m) => {
        const Icon = m.read ? MailOpen : (KIND_ICON[m.kind] || Mail);
        const color = KIND_COLOR[m.kind] || "#38bdf8";
        return (
          <button key={m.id} data-testid={`mail-${m.id.slice(0, 8)}`} onClick={() => openLetter(m)}
            className={`w-full rounded-2xl border p-3 flex items-center gap-3 text-left transition-colors hover:bg-white/10 ${m.read ? "bg-white/[0.03] border-white/5 opacity-70" : "bg-white/5 border-white/10"}`}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1e`, border: `1px solid ${color}40` }}>
              <Icon className="w-4 h-4" style={{ color }} />
            </span>
            <div className="flex-1 min-w-0">
              <p className={`font-sans text-sm truncate ${m.read ? "text-white/70" : "font-semibold text-white"}`}>{m.subject}</p>
              <p className="font-mono text-[8px] text-white/40 truncate">{m.from_name}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono text-[7px] text-white/30">{String(m.at).slice(5, 10)}</p>
              {!m.read && <span data-testid={`mail-unread-${m.id.slice(0, 8)}`} className="inline-block mt-1 w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />}
            </div>
          </button>
        );
      })}
    </div>
  );
}
